<script setup lang="ts">
/**
 * Application shell. Screens come from the router; this supplies the Vuetify
 * surface, the notification host, the shell that stands in for a screen that
 * has not resolved yet, and the two things that are true of every screen at
 * once: whether there is a connection, and whether there is a newer build.
 */
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import OfflineNotice from '@/components/OfflineNotice.vue'
import PatientLoader from '@/components/PatientLoader.vue'
import UpdatePrompt from '@/components/UpdatePrompt.vue'
import { useNotificationStore } from '@/stores/notification'

const notifications = useNotificationStore()
const router = useRouter()

/**
 * Whether the first navigation has settled.
 *
 * The household route's guard waits on the family context, so until it answers
 * RouterView has nothing to render. Without this, that wait is a blank page —
 * on the slowest and most common entry in the product, for somebody who
 * arrived from a text message and has no idea why nothing is happening
 * (SPEC §8.6.2).
 *
 * The static splash in index.html covers the window before this component
 * exists; this covers the window after Vue has replaced it.
 */
const routed = ref(false)

void router.isReady().then(() => {
  routed.value = true
})
</script>

<template>
  <v-app>
    <OfflineNotice />

    <v-main>
      <div v-if="!routed" class="shell">
        <p class="shell__name">Mabel's Table</p>
        <p class="shell__line">Plan the meal. Gather the family.</p>
        <PatientLoader label="your family" @retry="router.go(0)" />
      </div>

      <RouterView v-else />
    </v-main>

    <v-snackbar
      v-for="item in notifications.items"
      :key="item.id"
      :model-value="true"
      :color="item.kind"
      location="bottom"
      @update:model-value="notifications.dismiss(item.id)"
    >
      {{ item.message }}
    </v-snackbar>

    <UpdatePrompt />
  </v-app>
</template>

<style scoped>
/*
  The same shape as the static splash in index.html, so the handover from one
  to the other is invisible. Two things that look different here would read as
  the page loading twice.
*/
.shell {
  max-width: 34rem;
  margin: 0 auto;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100vh;
}

.shell__name {
  margin: 0 0 0.25rem;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.shell__line {
  margin: 0 0 2rem;
  color: rgb(var(--v-theme-on-surface-variant));
}
</style>
